// src/components/MeetingView.js
import React, { useRef, useState, useMemo } from 'react';
import AgendaItem from './agenda/AgendaItem';
import LivePollModal from './agenda/LivePollModal'; 

// === HELPER-KOMPONENTE FÜR DEN STATUS ===
const ParticipantStatus = ({ status }) => {
  switch (status) {
    case 'present':
      return (
        <span className="flex items-center text-sm font-medium text-green-600 dark:text-green-400 ml-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          (Zugesagt)
        </span>
      );
    case 'absent':
      return (
        <span className="flex items-center text-sm font-medium text-red-600 dark:text-red-400 ml-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
          (Abgelehnt/Verteiler)
        </span>
      );
    case 'pending':
      return (
        <span className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400 ml-2">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          (Ausstehend)
        </span>
      );
    default:
      if (status === 'joined_late' || status === 'left_early') {
        return <ParticipantStatus status="present" />;
      }
      return (
         <span className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400 ml-2">
           <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
             <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.79 4 4 0 .897-.29 1.723-.78 2.386l-.51.572c-.88.995-1.64 1.93-1.64 3.033V17.5a.5.5 0 01-1 0v-.683c0-.94.606-1.808 1.4-2.566l.2-.225a3 3 0 00 .86-2.046c0-1.657-1.343-3-3-3s-3 1.343-3 3c0 .17.01.336.03.497a.5.5 0 01-.998.058A4.002 4.002 0 018.228 9zM12 18a.96.96 0 00.96-.96c0-.53-.43-.96-.96-.96s-.96.43-.96.96c0 .53.43.96.96.96z" />
           </svg>
           (Status: {status})
         </span>
      );
  }
};


const MeetingView = ({ 
    meeting, 
    onBackToList, 
    setNotification, 
    isGuest = false, 
    allTasks = [], 
    companyUsers = [],
    currentUserId,
    companyId,
    onUpdateParticipantStatus,
    agendaProposals = [] 
}) => {
  const meetingContentRef = useRef(null); 
  const [showDeniedProposals, setShowDeniedProposals] = useState(false);

  // === FIX: Hooks (wie useMemo) müssen *immer* vor bedingten Returns aufgerufen werden ===
  const deniedProposals = useMemo(() => 
    (agendaProposals || []).filter(p => p.status === 'denied'), 
  [agendaProposals]);
  
  // === NEU: SPRACHEN AUFBEREITEN FÜR DIE ANZEIGE ===
  const languages = useMemo(() => {
        if (!meeting) return [];
        const uniqueLangs = new Set();
        uniqueLangs.add((meeting.originalLanguage || 'de').toLowerCase());

        if (Array.isArray(meeting.availableLanguages)) {
            meeting.availableLanguages.forEach(l => {
                if(l) uniqueLangs.add(l.toLowerCase());
            });
        }
        
        if (meeting.translationJobs && typeof meeting.translationJobs === 'object') {
            Object.entries(meeting.translationJobs).forEach(([lang, status]) => {
                if (String(status).toLowerCase() === 'completed') {
                    uniqueLangs.add(lang.toLowerCase());
                }
            });
        }

        return Array.from(uniqueLangs).map(l => l.toUpperCase()).sort();
  }, [meeting?.availableLanguages, meeting?.originalLanguage, meeting?.translationJobs]);

  // Berechnung des aktuellen Teilnehmers (Variable, kein Hook, daher sicher hier)
  const currentUserParticipant = (meeting?.allParticipants || []).find(p => p.uid === currentUserId);

  // Gast-Hydrierung
  const hydrateForGuest = (agendaItems) => {
      if (!Array.isArray(agendaItems)) return [];
      const tasksList = Array.isArray(allTasks) ? allTasks : [];
      return agendaItems.map(item => {
          const hydratedTasks = (item.taskIds || [])
              .map(id => tasksList.find(task => task.id === id))
              .filter(Boolean); 
          return {
              ...item,
              tasks: hydratedTasks,
              subPoints: Array.isArray(item.subPoints) ? hydrateForGuest(item.subPoints) : []
          };
      });
  };

  // Safe Agenda Access
  const finalAgenda = isGuest 
    ? hydrateForGuest(meeting?.agenda || []) 
    : (Array.isArray(meeting?.agenda) ? meeting.agenda : []);
  
  // Export-Funktion
  const exportProtocol = async (format) => {
    if (!meeting) return; // Safety check

    let exportText = `--- Besprechungsprotokoll ---\n\n`;
    exportText += `Titel: ${meeting.title}\n`;
    if (meeting.sequentialNumber) {
        exportText += `Protokoll-Nr.: P-${meeting.sequentialNumber}\n`;
    }
    exportText += `Datum: ${meeting.date}\n`;
    exportText += `Zeit: ${meeting.startTime} - ${meeting.endTime || 'Endzeit offen'}\n`;
    exportText += `Ort: ${meeting.location || 'Nicht angegeben'}\n`;
    exportText += `\nTeilnehmer:\n`;
    if (meeting.allParticipants && meeting.allParticipants.length > 0) {
      meeting.allParticipants.forEach(person => {
        let statusText = '';
        if (person.currentStatus === 'present') statusText = 'Zugesagt';
        if (person.currentStatus === 'absent') statusText = 'Abgelehnt/Verteiler';
        if (person.currentStatus === 'pending') statusText = 'Ausstehend';
        if (person.currentStatus === 'joined_late') statusText = 'Später beigetreten';
        if (person.currentStatus === 'left_early') statusText = 'Früher gegangen';
        exportText += `- ${person.name} (Status: ${statusText}`;
        (person.events || []).forEach(event => {
          exportText += `, ${event.type === 'joined' ? 'seit' : 'bis'} ${event.time}`;
        });
        exportText += `)\n`;
      });
    }
    exportText += `\n`;
    const appendAgendaItems = (items, level = 0) => {
      if (!Array.isArray(items)) return; 
      items.forEach(item => {
        const indent = '  '.repeat(level);
        exportText += `${indent}${item.number} ${item.description}\n`;
        if (item.notes) {
          exportText += `${indent}  Notizen: ${item.notes}\n`;
        }
        if (item.tasks && item.tasks.length > 0) {
          exportText += `${indent}  Aufgaben:\n`;
          item.tasks.forEach(task => {
            const assignedTo = task.assignees?.map(a => a.name).join(', ') || 'Niemand';
            const taskNumberPrefix = task.sequentialNumber ? `[T-${task.sequentialNumber}] ` : '';
            exportText += `${indent}    - ${taskNumberPrefix}${task.title}: ${task.description || ''} (Zugewiesen an: ${assignedTo}, Fällig: ${task.dueDate}`;
            if (task.project) {
              exportText += `, Projekt: ${task.project}`;
            }
            exportText += `, Status: ${task.status})\n`;
          });
        }
        if (item.subPoints && item.subPoints.length > 0) {
          appendAgendaItems(item.subPoints, level + 1);
        }
      });
    };
    appendAgendaItems(finalAgenda);
    if (format === 'clipboard') {
      const textarea = document.createElement('textarea');
      textarea.value = exportText;
      document.body.appendChild(textarea);
      textarea.select();
      try {
        document.execCommand('copy');
        if (setNotification) {
          setNotification({ message: 'Protokoll in die Zwischenablage kopiert!', type: 'success' });
        }
      } catch (err) {
        console.error('Kopieren fehlgeschlagen:', err);
        if (setNotification) {
          setNotification({ message: 'Kopieren fehlgeschlagen. Bitte manuell kopieren.', type: 'error' });
        }
      }
      document.body.removeChild(textarea);
    } else if (format === 'pdf') {
        // PDF Export Logik
    }
  };

  // === Handler für Zusage/Absage ===
  const handleRsvp = async (newStatus) => {
    if (!currentUserParticipant || isGuest || !meeting?.id) return;
    try {
      await onUpdateParticipantStatus(
        meeting.id, 
        currentUserParticipant.id, 
        newStatus
      );
    } catch (error) {
      console.error("Fehler beim RSVP-Update:", error);
      setNotification({ message: `Fehler: ${error.message}`, type: 'error' });
    }
  };

  // === EARLY RETURN NACH HOOKS ===
  if (!meeting) {
    return <div className="text-center mt-20">Protokoll wird geladen...</div>;
  }

  // Gesamtanzahl Teilnehmer für LivePollModal berechnen
  const totalParticipantsCount = meeting?.allParticipants?.length || 0;
  const allParticipantsList = meeting?.allParticipants || [];

  return (
    <div className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-black min-h-screen rounded-lg shadow-inner relative"> 
      
      {/* LivePollModal */}
      {!isGuest && companyId && meeting?.id && currentUserId && (
          <LivePollModal 
              companyId={companyId}
              meetingId={meeting.id}
              currentUserId={currentUserId}
              totalParticipants={totalParticipantsCount}
              setNotification={setNotification}
          />
      )}
      
      <h2 className="text-4xl font-extrabold mb-8 text-gray-900 dark:text-white text-center">Protokoll ansehen</h2>

      {/* === RSVP-BLOCK === */}
      {currentUserParticipant && !isGuest && (
        <div className="p-4 bg-blue-100 dark:bg-gray-700 rounded-xl mb-6 shadow-md border border-blue-200 dark:border-gray-600">
          <h4 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Ihre Teilnahme</h4>
          <div className="flex flex-wrap items-center gap-3">
            
            {currentUserParticipant.currentStatus !== 'present' && (
              <button 
                onClick={() => handleRsvp('present')}
                className="px-5 py-2 bg-green-600 text-white rounded-lg shadow hover:bg-green-700 transition duration-200 flex items-center"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Zusagen
              </button>
            )}

            {currentUserParticipant.currentStatus !== 'absent' && (
              <button 
                onClick={() => handleRsvp('absent')}
                className="px-5 py-2 bg-red-600 text-white rounded-lg shadow hover:bg-red-700 transition duration-200 flex items-center"
              >
                 <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Ablehnen
              </button>
            )}

            {currentUserParticipant.currentStatus === 'present' && (
               <p className="text-green-700 dark:text-green-300 font-medium">Sie haben zugesagt.</p>
            )}
            {currentUserParticipant.currentStatus === 'absent' && (
               <p className="text-red-700 dark:text-red-300 font-medium">Sie haben abgelehnt (im Verteiler).</p>
            )}
             {currentUserParticipant.currentStatus === 'pending' && (
               <p className="text-gray-700 dark:text-gray-300 font-medium">Bitte um Rückmeldung zu Ihrer Teilnahme.</p>
            )}
          </div>
        </div>
      )}

      <div ref={meetingContentRef} className="bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700 mb-8">
        <h3 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
            {meeting.sequentialNumber && (
                <span className="font-bold text-gray-500 dark:text-gray-400 mr-2">
                    [P-{meeting.sequentialNumber}]
                </span>
            )}
            {meeting.title}
        </h3>
        
        {/* === NEU: SPRACHEN ANZEIGEN IM PROTOKOLL === */}
        {languages.length > 0 && (
            <div className="flex items-center gap-1 mb-4">
                <span className="text-xs text-gray-500 mr-1">Verfügbar in:</span>
                {languages.map(lang => (
                    <span 
                        key={lang} 
                        className="inline-flex items-center justify-center px-2 py-0.5 rounded text-[10px] font-bold uppercase border border-indigo-200 dark:border-indigo-700 bg-indigo-50 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300"
                    >
                        {lang}
                    </span>
                ))}
            </div>
        )}

        <p className="text-gray-700 dark:text-gray-300 text-lg mb-2">
          <span className="font-semibold">Datum:</span> {meeting.date}
        </p>
        <p className="text-gray-700 dark:text-gray-300 text-lg mb-2">
          <span className="font-semibold">Zeit:</span> {meeting.startTime} - {meeting.endTime || 'Endzeit offen'}
        </p>
        <p className="text-gray-700 dark:text-gray-300 text-lg mb-2">
          <span className="font-semibold">Ort:</span> {meeting.location || 'Nicht angegeben'}
        </p>
        
        <h4 className="text-xl font-bold mb-4 text-gray-900 dark:text-white border-b pb-2 border-gray-200 dark:border-gray-700">Teilnehmer</h4>
        <div className="mb-4">
          
          {meeting.allParticipants && meeting.allParticipants.length > 0 ? (
            meeting.allParticipants.map(person => (
              <p key={person.id} className="text-gray-700 dark:text-gray-300 text-lg mb-1 flex items-center flex-wrap">
                {person.isExternal && <span title="Externer Gast" className="mr-2">🌍</span>}
                <span className="font-semibold mr-1">{person.isExternal ? person.email : person.name}:</span>
                <ParticipantStatus status={person.currentStatus} />
              </p>
            ))
          ) : ( 
            <p className="text-gray-500 italic">Keine Teilnehmerinformationen verfügbar.</p>
          )}

        </div>

        <h4 className="text-xl font-bold mb-4 text-gray-900 dark:text-white border-b pb-2 border-gray-200 dark:border-gray-700">Agendapunkte</h4>
        <div className="space-y-6">
          {finalAgenda.map((item) => (
            <AgendaItem
              key={item.id}
              item={item}
              level={0}
              isViewMode={true} 
              setNotification={setNotification}
              companyUsers={companyUsers} 
              teams={[]} 
              companyId={companyId}
              meetingId={meeting?.id}
              currentUserId={currentUserId}
              isModerator={false} 
              totalParticipants={totalParticipantsCount}
              allParticipants={allParticipantsList} 
              isExternal={meeting.isExternal || isGuest} 
            />
          ))}
        </div>
      </div>

      {/* === NEUE SEKTION: ABGELEHNTE ANTRÄGE (FÜR ALLE) === */}
      {deniedProposals.length > 0 && (
          <div className="bg-gray-100 dark:bg-gray-800 p-6 rounded-2xl shadow-xl border border-gray-200/50 dark:border-gray-700 mb-8">
              <h3 
                className="text-xl font-bold mb-4 text-gray-700 dark:text-gray-300 cursor-pointer flex justify-between items-center"
                onClick={() => setShowDeniedProposals(!showDeniedProposals)}
              >
                Abgelehnte Anträge ({deniedProposals.length})
                <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 transition-transform ${showDeniedProposals ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </h3>
              {showDeniedProposals && (
                <div className="space-y-3 mt-4">
                    {deniedProposals.map(proposal => (
                        <div key={proposal.id} className="flex items-center justify-between bg-white dark:bg-gray-700 p-4 rounded-lg shadow-inner opacity-70">
                            <div className="min-w-0">
                                <p className="font-medium text-gray-600 dark:text-gray-300 truncate line-through">{proposal.description}</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Eingereicht von: {proposal.proposedByName || 'N/A'}</p>
                            </div>
                        </div>
                    ))}
                </div>
              )}
          </div>
      )}

      <div className="flex justify-end space-x-4 pt-4">
        <button
          onClick={() => exportProtocol('pdf')}
          className="px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all duration-200 ease-in-out transform hover:scale-105 font-bold text-lg shadow-lg"
        >
          Protokoll als PDF exportieren
        </button>
        <button
          onClick={() => exportProtocol('clipboard')}
          className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-all duration-200 ease-in-out transform hover:scale-105 font-bold text-lg shadow-lg"
        >
          Protokoll in Zwischenablage kopieren
        </button>
        {!isGuest && (
            <button
              onClick={onBackToList}
              className="px-6 py-3 bg-gray-300 text-gray-800 rounded-xl hover:bg-gray-400 transition-all duration-200 ease-in-out transform hover:scale-105 font-bold text-lg shadow-md"
            >
              Zurück zur Liste
            </button>
        )}
      </div>
    </div>
  );
};

export default MeetingView;